import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import type { PartyMembershipRecoveryResponse } from "@bw/shared/types/generated";
import { PartyMembershipCleanupSheet } from "./PartyMembershipCleanupSheet";
import { useRemovePartyMembership } from "../hooks/useRemovePartyMembership";

const REASON_LABELS: Record<string, string> = {
  duplicate_active_membership: "Duplicate active membership",
  missing_active_admission: "Missing active check-in",
  stale_party_status: "Stale party status",
};

function safeName(member: {
  user_id: string;
  display_name?: string | null;
}): string {
  const name = member.display_name?.trim();
  return name && name !== member.user_id ? name : "Player";
}

function statusLabel(status: string): string {
  switch (status) {
    case "open":
      return "Open";
    case "queued":
      return "Queued";
    case "playing":
      return "Playing";
    default:
      return "Active";
  }
}

function linkageLabel(membership: PartyMembershipRecoveryResponse): string {
  if (membership.assignment_id) {
    const courtName = membership.court_name?.trim();
    return courtName ? `On court: ${courtName}` : "Assigned to a court";
  }
  switch (membership.entry_status) {
    case "waiting":
      return "Queue: Waiting";
    case "assigned":
      return "Queue: Assigned";
    default:
      return "Not in queue";
  }
}

function formatTimestamp(value: string | null | undefined): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

export function PartyMembershipRecoveryList({
  facilityId,
  authenticatedUserId,
  memberships,
  loading,
  error,
  onRetry,
  showEmptyMismatch = false,
  showViewParty = false,
  cleanupAllowed = true,
}: {
  facilityId: string;
  authenticatedUserId: string | undefined;
  memberships: PartyMembershipRecoveryResponse[] | undefined;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  showEmptyMismatch?: boolean;
  showViewParty?: boolean;
  cleanupAllowed?: boolean;
}) {
  const cleanup = useRemovePartyMembership(facilityId);
  const [selectedPartyId, setSelectedPartyId] = useState<string | null>(null);
  const [cleanupError, setCleanupError] = useState<string | null>(null);
  const selected =
    memberships?.find(
      (membership) => membership.party_id === selectedPartyId,
    ) ?? null;

  useEffect(() => {
    if (
      !selectedPartyId ||
      cleanup.isPending ||
      (cleanupError && loading) ||
      (!loading && !error && selected?.can_remove && cleanupAllowed)
    ) {
      return;
    }
    setSelectedPartyId(null);
    setCleanupError(null);
  }, [
    cleanup.isPending,
    cleanupError,
    cleanupAllowed,
    error,
    loading,
    selected?.can_remove,
    selectedPartyId,
  ]);

  if (loading) {
    return (
      <Stack
        direction="row"
        spacing={1}
        role="status"
        aria-live="polite"
        sx={{ alignItems: "center" }}
      >
        <CircularProgress size={18} />
        <Typography color="text.secondary">
          Loading party memberships…
        </Typography>
      </Stack>
    );
  }

  if (error) {
    return (
      <RecoveryError
        message="We couldn't load your parties. Please try again."
        action="Retry"
        onRetry={onRetry}
      />
    );
  }

  if (!memberships?.length) {
    return showEmptyMismatch ? (
      <RecoveryError
        message="Your party status doesn't match. Refresh before continuing."
        action="Refresh parties"
        onRetry={onRetry}
      />
    ) : null;
  }

  function openCleanup(membership: PartyMembershipRecoveryResponse) {
    if (!cleanupAllowed) return;
    cleanup.reset();
    setCleanupError(null);
    setSelectedPartyId(membership.party_id);
  }

  function closeCleanup() {
    if (cleanup.isPending) return;
    setSelectedPartyId(null);
    setCleanupError(null);
  }

  function confirmCleanup() {
    if (
      !selected?.can_remove ||
      !authenticatedUserId ||
      cleanup.isPending ||
      loading ||
      error ||
      !cleanupAllowed
    ) {
      return;
    }
    setCleanupError(null);
    cleanup.mutate(
      {
        partyId: selected.party_id,
        targetUserId: authenticatedUserId,
      },
      {
        onSuccess: () => setSelectedPartyId(null),
        onError: () =>
          setCleanupError(
            selected.is_leader
              ? "We couldn't disband this party. Please try again."
              : "We couldn't leave this party. Please try again.",
          ),
      },
    );
  }

  return (
    <Box component="section" aria-labelledby="party-recovery-title">
      <Typography variant="overline" color="warning.main">
        Needs attention
      </Typography>
      <Typography component="h2" variant="h5" id="party-recovery-title">
        Your parties
      </Typography>
      <Stack
        component="ul"
        spacing={1.5}
        sx={{ mt: 1.5, p: 0, listStyle: "none" }}
      >
        {memberships.map((membership) => {
          const createdAt = formatTimestamp(membership.party_created_at);
          const joinedAt = formatTimestamp(membership.membership_joined_at);
          return (
            <Paper
              component="li"
              key={membership.party_id}
              variant="outlined"
              sx={{ p: 2 }}
            >
              <Stack spacing={1.5}>
                <Stack
                  direction="row"
                  spacing={1}
                  useFlexGap
                  sx={{ flexWrap: "wrap", alignItems: "center" }}
                >
                  <Chip
                    size="small"
                    color="warning"
                    variant="outlined"
                    label={statusLabel(membership.party_status)}
                  />
                  <Typography variant="body2">
                    {linkageLabel(membership)}
                  </Typography>
                  {createdAt ? (
                    <Typography
                      component="time"
                      variant="caption"
                      color="text.secondary"
                      dateTime={membership.party_created_at}
                    >
                      Created {createdAt}
                    </Typography>
                  ) : null}
                  {joinedAt && joinedAt !== createdAt ? (
                    <Typography
                      component="time"
                      variant="caption"
                      color="text.secondary"
                      dateTime={membership.membership_joined_at}
                    >
                      Joined {joinedAt}
                    </Typography>
                  ) : null}
                </Stack>

                <Stack
                  component="ul"
                  aria-label="Party members"
                  direction="row"
                  spacing={0.75}
                  useFlexGap
                  sx={{ p: 0, flexWrap: "wrap", listStyle: "none" }}
                >
                  {membership.member_details.map((member) => (
                    <Typography
                      component="li"
                      variant="body2"
                      key={member.user_id}
                    >
                      {safeName(member)}
                    </Typography>
                  ))}
                </Stack>

                {membership.integrity_reasons.length ? (
                  <Stack
                    component="ul"
                    aria-label="Issues"
                    spacing={0.25}
                    sx={{ m: 0, pl: 2.5 }}
                  >
                    {membership.integrity_reasons.map((reason) => (
                      <Typography
                        component="li"
                        variant="body2"
                        color="warning.main"
                        key={reason}
                      >
                        {REASON_LABELS[reason] ?? "Membership needs review"}
                      </Typography>
                    ))}
                  </Stack>
                ) : null}

                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  sx={{ alignItems: { sm: "center" } }}
                >
                  {showViewParty ? (
                    <Button
                      component={Link}
                      aria-label={`View current party with ${membership.member_details.map(safeName).join(", ")}`}
                      variant="outlined"
                      size="small"
                      to={`/dropin/party/${membership.party_id}`}
                    >
                      View current party
                    </Button>
                  ) : null}
                  {membership.can_remove ? (
                    <Button
                      type="button"
                      color="error"
                      variant="outlined"
                      size="small"
                      onClick={() => openCleanup(membership)}
                      disabled={!authenticatedUserId || !cleanupAllowed}
                    >
                      {membership.is_leader ? "Disband party" : "Leave party"}
                    </Button>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Finish the active queue or court session first
                    </Typography>
                  )}
                </Stack>
              </Stack>
            </Paper>
          );
        })}
      </Stack>

      {selected?.can_remove && cleanupAllowed ? (
        <PartyMembershipCleanupSheet
          action={selected.is_leader ? "disband" : "leave"}
          onConfirm={confirmCleanup}
          onCancel={closeCleanup}
          pending={cleanup.isPending}
          error={cleanupError}
          memberNames={selected.member_details.map(safeName)}
          createdAt={formatTimestamp(selected.party_created_at)}
          joinedAt={formatTimestamp(selected.membership_joined_at)}
        />
      ) : null}
    </Box>
  );
}

function RecoveryError({
  message,
  action,
  onRetry,
}: {
  message: string;
  action: string;
  onRetry: () => void;
}) {
  return (
    <Stack spacing={1} sx={{ alignItems: "flex-start" }}>
      <Alert severity="error">{message}</Alert>
      <Button variant="outlined" size="small" onClick={onRetry}>
        {action}
      </Button>
    </Stack>
  );
}
